/* eslint-disable no-console */
// src/fcmService.js
import { messagingPromise, getToken, onMessage } from './firebase';
import { callApi } from './utils/apiCall';
// Fingerprint storage lives in a tiny dependency-free module so that
// src/utils/jwt.js can clear it from setSession() without importing this
// file (which pulls in firebase + apiCall and risks an import cycle).
// Re-exported here for existing call sites.
import { FCM_FINGERPRINT_STORAGE_KEY, clearFCMRegistrationFingerprint } from './utils/fcmFingerprintStorage';

export { FCM_FINGERPRINT_STORAGE_KEY, clearFCMRegistrationFingerprint };

const VAPID_KEY = import.meta.env.VITE_REACT_APP_VAPID_KEY;

// What this browser can show (CU-869fmcc9r): the backend only sends incoming-call pushes to
// devices registered with this marker, so an older service worker never gets one (it would
// show "Dental Sem Update!"). Changing it re-registers every browser once.
export const FCM_DEVICE_CAPABILITY = 'call-alerts-v1';
export const FCM_DEVICE_NAME = `Web Device · ${FCM_DEVICE_CAPABILITY}`;

// Skip-guard: avoids re-POSTing /api/v1/fcm/devices/ (~929ms avg, Sentry) when
// nothing about the registration has actually changed since the last success.
const FINGERPRINT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — browsers rotate tokens periodically

const readStoredFingerprint = () => {
    try {
        const raw = localStorage.getItem(FCM_FINGERPRINT_STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (err) {
        return null;
    }
};

const writeStoredFingerprint = (fingerprint) => {
    try {
        localStorage.setItem(FCM_FINGERPRINT_STORAGE_KEY, JSON.stringify(fingerprint));
    } catch (err) {
        // ignore storage errors (private mode / quota exceeded)
    }
};

const shouldSkipRegistration = (stored, candidate) => {
    if (!stored) return false;
    // Never skip after a previously failed attempt.
    if (stored.success !== true) return false;
    if (stored.userId !== candidate.userId) return false;
    if (stored.practiceId !== candidate.practiceId) return false;
    if (stored.registrationToken !== candidate.registrationToken) return false;
    if (stored.capability !== candidate.capability) return false;
    if (typeof stored.registeredAt !== 'number') return false;
    if (Date.now() - stored.registeredAt >= FINGERPRINT_MAX_AGE_MS) return false;
    return true;
};

export const requestAndRegisterFCMToken = async (currentPractice, userId = null) => {
    try {
        const messaging = await messagingPromise;
        if (!messaging) return;

        const permission = await Notification.requestPermission();

        if (permission !== 'granted') {
            console.warn("Notification permission not granted.");
            return;
        }

        const token = await getToken(messaging, { vapidKey: VAPID_KEY });

        const currentPracticeFromLocalStorage = localStorage.getItem("currentPractice");

        // Generate or fetch device_id
        let deviceId = localStorage.getItem("device_id");
        if (!deviceId || `${currentPracticeFromLocalStorage}` !== `${currentPractice}`) {
            deviceId = crypto.randomUUID();
            localStorage.setItem("device_id", deviceId);
            localStorage.setItem("currentPractice", currentPractice);
        }

        const candidateFingerprint = {
            userId: userId != null ? String(userId) : null,
            practiceId: currentPractice != null ? String(currentPractice) : null,
            registrationToken: token,
            capability: FCM_DEVICE_CAPABILITY,
        };

        if (shouldSkipRegistration(readStoredFingerprint(), candidateFingerprint)) {
            return;
        }

        // Register device with your backend
        const response = await callApi('/api/v1/fcm/devices/', 'POST',
            {
                name: FCM_DEVICE_NAME,
                registration_id: token,
                device_id: deviceId,
                active: true,
                type: "web",
                practice: currentPractice
            },
        );

        // Only remember success. Writing on a failed POST would let the
        // skip-guard treat a NEXT identical attempt as "already done" —
        // shouldSkipRegistration() does check stored.success, but there's no
        // reason to persist a failed fingerprint at all: keep the guard's
        // stored state limited to attempts that actually registered the device.
        if (response?.ok) {
            writeStoredFingerprint({
                ...candidateFingerprint,
                registeredAt: Date.now(),
                success: true,
            });
        }
    } catch (err) {
        console.error("FCM registration error:", err);
    }
};

// Defers requestAndRegisterFCMToken() off the mount-effect critical path.
// Prefers requestIdleCallback (bounded by its own `timeout`) so it never
// competes with the boot-time request pool; falls back to a bounded
// setTimeout for browsers without requestIdleCallback, and always keeps a
// hard ~5s fallback so registration still runs even if idle never fires.
export const scheduleFCMRegistration = (currentPractice, userId = null) => {
    let hasRun = false;
    let idleCallbackId = null;
    let fallbackTimeoutId = null;
    let hardTimeoutId = null;

    const clearAllTimers = () => {
        if (idleCallbackId != null && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
            window.cancelIdleCallback(idleCallbackId);
        }
        if (fallbackTimeoutId != null) clearTimeout(fallbackTimeoutId);
        if (hardTimeoutId != null) clearTimeout(hardTimeoutId);
    };

    const run = () => {
        if (hasRun) return;
        hasRun = true;
        clearAllTimers();
        requestAndRegisterFCMToken(currentPractice, userId);
    };

    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        idleCallbackId = window.requestIdleCallback(run, { timeout: 3000 });
    } else {
        fallbackTimeoutId = setTimeout(run, 3000);
    }
    // Hard fallback: guarantees registration runs within ~5s even if the
    // idle callback never fires (e.g. a continuously busy main thread).
    hardTimeoutId = setTimeout(run, 5000);

    // Cancel on unmount / practice change — mark as run so a late-firing
    // idle callback (already queued by the browser) becomes a no-op.
    return () => {
        hasRun = true;
        clearAllTimers();
    };
};

// Handle foreground notifications
export const listenForForegroundMessages = async (showCustomNotification) => {
    try {
        const messaging = await messagingPromise;
        if (!messaging) return;

        onMessage(messaging, (payload) => {
            showCustomNotification(payload);
        });
    } catch (err) {
        console.error("FCM foreground listener error:", err);
    }
};